import type { Role } from './types';

/**
 * Credential + principal storage for the SPA, backed by `sessionStorage` so a
 * hard page refresh doesn't sign the user out (BUI-tracked gap — previously
 * in-memory only, see docs/frontend.md). `sessionStorage` rather than
 * `localStorage`: it survives a reload but clears when the tab/browser
 * closes, which is a reasonable session boundary and keeps the token's
 * lifetime bounded without needing a refresh-token/cookie flow on the API
 * side (still not implemented — see docs/frontend.md).
 */

type Listener = () => void;

export interface StoredPrincipal {
  tenantId: string;
  userId: string;
  role: Role | undefined;
}

/** Snapshot of the BUI staff member's own session, kept aside while they're "switched in" to a
 * customer workspace (BUI Super-Admin Console) so "Exit to Admin Console" can restore it exactly
 * — never persisted anywhere but this session, and only ever set/read by the impersonation flow. */
export interface ImpersonationOrigin {
  accessToken: string | null;
  apiKey: string | null;
  principal: StoredPrincipal;
  workspaceName: string;
}

interface StoredSession {
  accessToken: string | null;
  apiKey: string | null;
  principal: StoredPrincipal | null;
  impersonationOrigin: ImpersonationOrigin | null;
}

const SESSION_KEY = 'defendiq.session';
const EMPTY_SESSION: StoredSession = { accessToken: null, apiKey: null, principal: null, impersonationOrigin: null };

function readSession(): StoredSession {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return { ...EMPTY_SESSION };
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    return {
      accessToken: parsed.accessToken ?? null,
      apiKey: parsed.apiKey ?? null,
      principal: parsed.principal ?? null,
      impersonationOrigin: parsed.impersonationOrigin ?? null,
    };
  } catch {
    return { ...EMPTY_SESSION };
  }
}

function writeSession(next: StoredSession): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage unavailable (e.g. a strict private-browsing mode) —
    // fall back to in-memory-only behavior for this call rather than
    // throwing; the in-memory `session` variable below still works for the
    // lifetime of the page.
  }
}

let session: StoredSession = readSession();
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getAuthHeader(): Record<string, string> {
  if (session.apiKey) return { 'X-API-Key': session.apiKey };
  if (session.accessToken) return { Authorization: `Bearer ${session.accessToken}` };
  return {};
}

export function hasCredentials(): boolean {
  return Boolean(session.accessToken || session.apiKey);
}

/** Raw credential getters — used only by the impersonation flow (`auth-context.tsx`'s
 * `impersonate`) to stash the BUI staff member's OWN current credential before switching the
 * active session to an impersonation token, so "Exit to Admin Console" can restore it exactly. */
export function getAccessToken(): string | null {
  return session.accessToken;
}
export function getApiKey(): string | null {
  return session.apiKey;
}

export function getStoredPrincipal(): StoredPrincipal | null {
  return session.principal;
}

export function setAccessToken(token: string): void {
  session = { ...session, accessToken: token, apiKey: null };
  writeSession(session);
  notify();
}

export function setApiKey(key: string): void {
  session = { ...session, accessToken: null, apiKey: key };
  writeSession(session);
  notify();
}

export function setStoredPrincipal(principal: StoredPrincipal | null): void {
  session = { ...session, principal };
  writeSession(session);
  notify();
}

export function clearCredentials(): void {
  session = { ...EMPTY_SESSION };
  writeSession(session);
  notify();
}

export function getImpersonationOrigin(): ImpersonationOrigin | null {
  return session.impersonationOrigin;
}

export function setImpersonationOrigin(origin: ImpersonationOrigin | null): void {
  session = { ...session, impersonationOrigin: origin };
  writeSession(session);
  notify();
}

export function subscribeToCredentials(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
